// engagement-components/ChannelFallback.jsx
// Configurable channel priority chain: WhatsApp → SMS → Email
// Used in JourneyBuilder steps and ProfileCompletionBuilder

import React from "react";
import { Card, Form, Badge, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaWhatsapp, FaSms, FaEnvelope, FaArrowRight, FaGripVertical, FaExclamationTriangle } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// ─── Channel definitions ──────────────────────────────────────────────────────
export const CHANNEL_DEFS = {
  WhatsApp: {
    icon:        <FaWhatsapp />,
    color:       "#198754",
    bg:          "#d1fae5",
    requires:    "phone",
    requiresLabel:"phone number",
    creditCost:  1,
  },
  SMS: {
    icon:        <FaSms />,
    color:       "#6c757d",
    bg:          "#f3f4f6",
    requires:    "phone",
    requiresLabel:"phone number",
    creditCost:  1,
  },
  Email: {
    icon:        <FaEnvelope />,
    color:       "#0d6efd",
    bg:          "#dbeafe",
    requires:    "email",
    requiresLabel:"email address",
    creditCost:  2,
  },
};

// Default priority order
export const DEFAULT_FALLBACK = [
  { channel: "WhatsApp", enabled: true,  fallback: true  },
  { channel: "SMS",      enabled: true,  fallback: true  },
  { channel: "Email",    enabled: true,  fallback: true  },
];

// ─── Channel Fallback Component ───────────────────────────────────────────────
// props:
//   chain    = [{ channel, enabled, fallback }]
//   setChain = setter
//   mode     = "full" (all 3) | "compact" (inline pill selector)
//   label    = optional section label override

const ChannelFallback = ({ chain = DEFAULT_FALLBACK, setChain, mode = "full", label }) => {

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = Array.from(chain);
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setChain(arr);
  };

  const toggleEnabled = (idx) =>
    setChain(chain.map((c, i) => i === idx ? { ...c, enabled: !c.enabled } : c));

  const toggleFallback = (idx) =>
    setChain(chain.map((c, i) => i === idx ? { ...c, fallback: !c.fallback } : c));

  const enabledChannels = chain.filter((c) => c.enabled);

  if (mode === "compact") {
    // Inline pill selector — used inside step cards
    return (
      <div className="d-flex flex-wrap gap-1 align-items-center">
        {chain.map((c, idx) => {
          const def = CHANNEL_DEFS[c.channel];
          return (
            <button
              key={c.channel}
              onClick={() => toggleEnabled(idx)}
              className="border-0 rounded-pill px-2 py-1 d-flex align-items-center gap-1"
              style={{
                background: c.enabled ? def.bg : "#f3f4f6",
                color:      c.enabled ? def.color : "#9ca3af",
                fontSize:   "0.76rem",
                fontWeight: c.enabled ? 600 : 400,
                cursor:     "pointer",
                transition: "all 0.15s",
              }}
              aria-pressed={c.enabled}
              title={c.enabled ? `Disable ${c.channel}` : `Enable ${c.channel}`}
            >
              {React.cloneElement(def.icon, { size: 11 })}
              {c.channel}
            </button>
          );
        })}
        {enabledChannels.length > 1 && (
          <span className="text-muted" style={{ fontSize: "0.7rem" }}>
            (fallback order)
          </span>
        )}
      </div>
    );
  }

  // Full mode — card with drag-drop ordering
  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="fw-bold mb-0">
            {label || (
              <>
                <i className="fas fa-network-wired text-primary me-2"></i>
                Channel Priority & Fallback
              </>
            )}
          </h6>
          <OverlayTrigger placement="left" overlay={
            <Tooltip style={{ maxWidth: 280 }}>
              The system tries Channel 1 first. If the guest has no data for it (e.g. no WhatsApp number), 
              it falls back to Channel 2, then Channel 3. Drag to reorder.
            </Tooltip>
          }>
            <i className="fas fa-question-circle text-muted" style={{ cursor: "help" }}></i>
          </OverlayTrigger>
        </div>
      </Card.Header>
      <Card.Body className="p-4">

        <p className="text-muted small mb-3">
          Messages are sent through the <strong>first enabled channel</strong> for which the guest has valid data.
          Drag to change priority. Toggle to enable/disable a channel.
        </p>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="channel-fallback">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {chain.map((item, idx) => {
                  const def = CHANNEL_DEFS[item.channel];
                  return (
                    <Draggable key={item.channel} draggableId={item.channel} index={idx}>
                      {(prov, snap) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className="d-flex align-items-center gap-3 p-3 rounded border mb-2"
                          style={{
                            background:  snap.isDragging ? "#f0f4ff" : item.enabled ? def.bg : "#f9fafb",
                            borderColor: snap.isDragging ? "#6366f1" : item.enabled ? def.color + "44" : "#e5e7eb",
                            opacity:     item.enabled ? 1 : 0.5,
                            transition:  "all 0.15s",
                          }}
                        >
                          {/* Priority number */}
                          <div
                            className="d-flex align-items-center justify-content-center fw-bold rounded-circle flex-shrink-0"
                            style={{
                              width: 28, height: 28,
                              background: item.enabled ? def.color : "#e5e7eb",
                              color:      item.enabled ? "#fff" : "#9ca3af",
                              fontSize:   "0.8rem",
                            }}
                          >
                            {idx + 1}
                          </div>

                          {/* Drag handle */}
                          <div {...prov.dragHandleProps} style={{ cursor: "grab", color: "#9ca3af" }}>
                            <FaGripVertical size={14} />
                          </div>

                          {/* Channel info */}
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ color: def.color }}>
                                {React.cloneElement(def.icon, { size: 16 })}
                              </span>
                              <span className="fw-semibold" style={{ color: item.enabled ? def.color : "#6b7280" }}>
                                {item.channel}
                              </span>
                              {idx === 0 && item.enabled && (
                                <Badge bg="success" style={{ fontSize: "0.65rem" }}>Primary</Badge>
                              )}
                              {idx > 0 && item.enabled && item.fallback && (
                                <Badge bg="secondary" style={{ fontSize: "0.65rem" }}>Fallback {idx}</Badge>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                              Requires: <code>{def.requiresLabel}</code>
                              &nbsp;·&nbsp; {def.creditCost} credit{def.creditCost > 1 ? "s" : ""} per message
                            </div>
                          </div>

                          {/* Fallback toggle (only for non-primary) */}
                          {idx > 0 && item.enabled && (
                            <Form.Check
                              type="switch"
                              id={`fallback-${item.channel}`}
                              label={<span style={{ fontSize: "0.75rem" }}>Use as fallback</span>}
                              checked={item.fallback}
                              onChange={() => toggleFallback(idx)}
                            />
                          )}

                          {/* Enable/disable toggle */}
                          <Form.Check
                            type="switch"
                            id={`enable-${item.channel}`}
                            checked={item.enabled}
                            onChange={() => toggleEnabled(idx)}
                            aria-label={`${item.enabled ? "Disable" : "Enable"} ${item.channel}`}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Live preview of the logic */}
        <div className="mt-3 p-3 rounded" style={{ background: "#f8faff", border: "1px solid #c7d2fe", fontSize: "0.8rem" }}>
          <div className="fw-semibold text-primary mb-2">
            <i className="fas fa-route me-2"></i>Delivery logic for each guest:
          </div>
          <div className="d-flex align-items-center flex-wrap gap-1">
            {chain.filter((c) => c.enabled).map((c, i, arr) => {
              const def = CHANNEL_DEFS[c.channel];
              return (
                <React.Fragment key={c.channel}>
                  <span className="px-2 py-1 rounded fw-semibold"
                    style={{ background: def.bg, color: def.color, fontSize: "0.76rem" }}>
                    {React.cloneElement(def.icon, { size: 11, className: "me-1" })}
                    {c.channel}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.72rem" }}>
                      <FaArrowRight size={9} />
                      if no {CHANNEL_DEFS[c.channel].requiresLabel}
                      <FaArrowRight size={9} />
                    </span>
                  )}
                </React.Fragment>
              );
            })}
            {enabledChannels.length === 0 && (
              <span className="text-danger">
                <FaExclamationTriangle className="me-1" size={12} />
                No channels enabled — guests will not receive messages
              </span>
            )}
          </div>
          {enabledChannels.length > 0 && (
            <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
              If a guest has no valid data for any enabled channel, the message is skipped and flagged.
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ChannelFallback;
